import React, { useState, useEffect } from "react"
import {
    Button,
    Modal,
    ModalHeader,
    ModalBody,
    ModalFooter
} from "reactstrap"
import ReactCrop, { centerCrop, makeAspectCrop } from "react-image-crop"
import "react-image-crop/dist/ReactCrop.css"

/**
 * CropModal Component for Image Cropping
 * 
 * A modal component that handles image cropping with ReactCrop
 * Features:
 * - 1000:258 aspect ratio cropping
 * - Apply and Cancel actions
 * - Proper cleanup of blob URLs
 * - Preserves original file extension
 */
const CropModal = ({ isOpen, toggle, imageSrc, onCropComplete, originalFile }) => {
    const [crop, setCrop] = useState(null)
    const [completedCrop, setCompletedCrop] = useState(null)
    const [imageRef, setImageRef] = useState(null)

    // Reset crop when modal opens with new image
    useEffect(() => {
        if (isOpen && imageSrc) {
            setCrop(null)
            setCompletedCrop(null)
            setImageRef(null)
        }
    }, [isOpen, imageSrc])

    const getCroppedImg = (image, crop, fileName) => {
        const canvas = document.createElement('canvas')
        const ctx = canvas.getContext('2d')

        if (!crop || !image || !crop.width || !crop.height) {
            return null
        }

        const scaleX = image.naturalWidth / image.width
        const scaleY = image.naturalHeight / image.height
        const pixelRatio = window.devicePixelRatio
        console.log('scaleX =>',scaleX)
        console.log('scaleY =>',scaleY)
        console.log('pixelRatio =>',pixelRatio)

        canvas.width = crop.width * pixelRatio * scaleX
        canvas.height = crop.height * pixelRatio * scaleY

        ctx.setTransform(pixelRatio, 0, 0, pixelRatio, 0, 0)
        ctx.imageSmoothingQuality = 'high'

        ctx.drawImage(
            image,
            crop.x * scaleX,
            crop.y * scaleY,
            crop.width * scaleX,
            crop.height * scaleY,
            0,
            0,
            crop.width * scaleX,
            crop.height * scaleY
        )

        return new Promise((resolve) => {
            canvas.toBlob(
                (blob) => {
                    if (!blob) {
                        console.error('Canvas is empty')
                        return
                    }
                    
                    // Create a proper File object with original extension
                    const originalExtension = originalFile ? 
                        originalFile.name.split('.').pop().toLowerCase() : 'jpg'
                    
                    const croppedFile = new File(
                        [blob], 
                        fileName || `cropped-image.${originalExtension}`, 
                        {
                            type: blob.type || `image/${originalExtension}`,
                            lastModified: new Date().getTime()
                        }
                    )
                    
                    resolve(croppedFile)
                },
                'image/jpeg',
                1
            )
        })
    }

    const handleApplyCrop = async () => {
        if (imageRef && completedCrop?.width && completedCrop?.height) {
            try {
                const croppedImage = await getCroppedImg(imageRef, completedCrop, 'cropped-banner.jpg')
                if (croppedImage) {
                    const croppedImageUrl = URL.createObjectURL(croppedImage)
                    onCropComplete(croppedImage, croppedImageUrl)
                    toggle()
                }
            } catch (error) {
                console.error('Error during image cropping:', error)
            }
        }
    }

    const onImageLoad = (e) => {
        const { naturalWidth: width, naturalHeight: height } = e.currentTarget
        setImageRef(e.currentTarget)

        // Create initial crop with 1000:258 aspect ratio
        const newCrop = centerCrop(
            makeAspectCrop(
                {
                    unit: '%',
                    width: 90,
                },
                1000 / 258, // 1000:258 aspect ratio
                width,
                height
            ),
            width,
            height
        )

        // Ensure all crop values are defined
        if (newCrop && typeof newCrop.x === 'number' && typeof newCrop.y === 'number') {
            setCrop(newCrop)
            setCompletedCrop(newCrop)
        }
    }

    return (
        <Modal isOpen={isOpen} toggle={toggle} size="lg" className="crop-modal">
            <ModalHeader toggle={toggle}>Crop Image</ModalHeader>
            <ModalBody className="d-flex justify-content-center align-items-center">
                
                {imageSrc && (
                    <div style={{ maxWidth: '100%', margin: '0 auto' }}>
                        {crop && (
                            <ReactCrop
                                crop={crop}
                                onChange={(_, percentCrop) => setCrop(percentCrop)}
                                onComplete={(crop) => setCompletedCrop(crop)}
                                aspect={1000/258}
                                style={{
                                    maxWidth: '100%',
                                }}
                                className="custom-crop"
                            >
                                <img 
                                    src={imageSrc} 
                                    onLoad={onImageLoad}
                                    crossOrigin="anonymous"
                                    style={{ 
                                        width: '100%', 
                                        height: 'auto', 
                                        maxHeight: '400px'
                                    }}
                                    alt="Crop preview"
                                />
                            </ReactCrop>
                        )}
                        {!crop && (
                            <img 
                                src={imageSrc} 
                                onLoad={onImageLoad}
                                crossOrigin="anonymous"
                                style={{ 
                                    width: '100%', 
                                    height: 'auto', 
                                    maxHeight: '400px'
                                }}
                                alt="Loading..."
                            />
                        )}
                    </div>
                )}
            </ModalBody>
            <ModalFooter>
                <Button color="secondary" onClick={toggle} outline>
                    Cancel
                </Button>
                <Button 
                    color="primary" 
                    onClick={handleApplyCrop}
                    disabled={!completedCrop?.width || !completedCrop?.height}
                >
                    Save
                </Button>
            </ModalFooter>
        </Modal>
    )
}

export default CropModal 